import * as React from 'react';
import MuiMenuItem, { type MenuItemProps as MuiMenuItemProps } from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Icon from '@mui/material/Icon';
import type { BadgeProps } from '@mui/material/Badge';
import Badge from './Badge';

export interface MenuItemProps extends Omit<MuiMenuItemProps, 'value'> {
  /** react-toolbox: menu item text */
  caption?: string;
  /** react-toolbox: icon name or element */
  icon?: React.ReactNode;
  /** react-toolbox: shortcut text */
  shortcut?: string;
  /** react-toolbox: item value */
  value?: string;
  /** react-toolbox: ripple effect (inverted - false means disable) */
  ripple?: boolean;
  /** react-toolbox: selected state */
  selected?: boolean;
  /** badge content — string */
  badge?: string;
  /** MUI Badge color */
  badgeColor?: BadgeProps['color'];
}

/**
 * MenuItem wrapper - maps react-toolbox MenuItem to MUI MenuItem
 * - `caption` maps to primary text content
 * - `icon` (string) renders as material Icon in ListItemIcon
 * - `ripple={false}` maps to disableRipple
 */
const MenuItem = React.forwardRef<HTMLLIElement, MenuItemProps>(
  ({ caption, icon, shortcut, value, ripple = true, badge, badgeColor = 'error', children, ...rest }, ref) => {
    const disableRipple = ripple === false;
    const wrapWithBadge = (node: React.ReactNode) =>
      badge !== undefined ? (
        <Badge
          content={badge}
          color={badgeColor}
          sx={{ '& .MuiBadge-badge': { fontSize: '0.5625rem', lineHeight: 1, width: 16, minWidth: 16, height: 16 } }}
        >
          {node as React.ReactElement}
        </Badge>
      ) : (
        node
      );

    // Render icon
    const iconElement =
      icon && typeof icon === 'string' ? (
        <ListItemIcon>{wrapWithBadge(<Icon>{icon}</Icon>)}</ListItemIcon>
      ) : icon ? (
        <ListItemIcon>{wrapWithBadge(icon as React.ReactElement)}</ListItemIcon>
      ) : null;

    // Check if children is a single Link element (has a 'to' prop)
    const isLinkChild = React.isValidElement<{ to?: unknown }>(children) && children.props.to !== undefined;

    // If we're dealing with a link, we want that link to wrap all of the children
    // so that the whole row is clickable
    if (isLinkChild) {
      const linkChild = children as React.ReactElement<{ to?: unknown; children?: React.ReactNode }>;
      const linkContent = linkChild.props.children;
      const linkText = linkContent ? <ListItemText primary={linkContent} secondary={shortcut} /> : null;
      const renderedLinkText = !iconElement && badge !== undefined ? wrapWithBadge(linkText) : linkText;
      const wrappedLink = React.cloneElement(linkChild, {
        ...linkChild.props,
        children: (
          <>
            {iconElement}
            {renderedLinkText}
          </>
        ),
      });

      return (
        <MuiMenuItem
          ref={ref}
          disableRipple={disableRipple}
          data-value={value}
          className="menu-item-link"
          {...rest}
          sx={{ padding: 0 }}
        >
          {wrappedLink}
        </MuiMenuItem>
      );
    }

    // Content: use caption or children
    const textContent = caption || children;

    // If no icon, wrap the text with the badge instead
    const textElement = textContent ? <ListItemText primary={textContent} secondary={shortcut} /> : null;
    const renderedText = !iconElement && badge !== undefined ? wrapWithBadge(textElement) : textElement;

    return (
      <MuiMenuItem ref={ref} disableRipple={disableRipple} data-value={value} {...rest}>
        {iconElement}
        {renderedText}
      </MuiMenuItem>
    );
  },
);

MenuItem.displayName = 'MenuItem';

export default MenuItem;
